<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useSSRContext } from "vue";
import { useRequestHeaders } from "#app";
import { useMainStore } from "@/stores/main";
import { storeToRefs } from "pinia";

const mainStore = useMainStore();

// Import composable for more reliable mobile detection
import { useDisplay } from "@/composables/useDisplay";

// Use the platform's display detection for more reliability
const { isMobile } = useDisplay();

// Additional mobile detection using CSS media queries
// This adds a second layer of protection to ensure the footer is hidden on mobile
// Important: For hydration consistency, we initialize to false and only update in client
const isSmallScreen = ref(false);

// Indicate if we're running in a React Native environment
// Initialize as false for SSR to ensure consistent hydration
const isReactNative = ref(false);

// Show footer only on desktop - this computed property ensures consistent rendering
const showFooter = computed(() => {
  // During SSR, be conservative and don't show footer
  // This way client and server will render the same thing
  if (import.meta.server) return false;
  return !isMobile.value && !isSmallScreen.value && !isReactNative.value;
});

onMounted(() => {
  // Set initial screen size check
  updateScreenSize();

  // Add event listener for resize
  window.addEventListener("resize", updateScreenSize);

  // Initialize React Native detection (client-side only)
  const { $reactNative } = useNuxtApp();
  isReactNative.value = $reactNative?.isApp || false;
  console.log(
    "Footer: Using React Native plugin detection:",
    isReactNative.value
  );
});

onUnmounted(() => {
  // Clean up event listener
  window.removeEventListener("resize", updateScreenSize);
});

// Function to check screen size using CSS media queries
function updateScreenSize() {
  isSmallScreen.value = window.matchMedia("(max-width: 768px)").matches;
  console.log(
    "Footer: Screen size check - isSmallScreen:",
    isSmallScreen.value
  );
}

const { globalSettings } = storeToRefs(mainStore);
const footerNavigation: any = computed(() => globalSettings.value?.footer);

// About section toggle for mobile
const isAboutOpen = ref(false);
const toggleAbout = () => {
  isAboutOpen.value = !isAboutOpen.value;
};
</script>

<template>
  <!-- Footer is hidden on:  
    1. Mobile devices (via isMobile detection)
    2. Small screens (via CSS media query)
    3. React Native apps (via isReactNative ref)
    Using multiple detection methods ensures it's never shown on mobile
    We use ClientOnly to ensure consistent hydration between server and client
  -->
  <ClientOnly>
    <footer
      v-if="showFooter"
      class="flex justify-center items-center self-stretch px-16 py-16 w-full text-sm leading-5 text-white bg-footer max-md:px-5 max-md:max-w-full"
    >
      <div
        class="flex flex-col md:flex-row gap-12 justify-between items-start max-w-full w-[1125px] max-md:flex-wrap"
      >
        <!-- Left Column -->
        <ClientOnly>
          <nav v-if="footerNavigation?.[0]?.links" class="flex flex-col">
            <template
              v-for="(link, linkIndex) in footerNavigation[0].links"
              :key="linkIndex"
            >
              <BaseLink class="mt-3" :link="link">
                <span class="link-hover-effect">{{ link.linkText }}</span>
              </BaseLink>
            </template>
          </nav>
        </ClientOnly>

        <!-- Rest of the footer template -->
        <!-- Rest of nightly branch's footer content will be included here -->
        <!-- Middle Column -->
        <ClientOnly>
          <nav v-if="footerNavigation?.[1]?.links" class="flex flex-col">
            <template
              v-for="(link, linkIndex) in footerNavigation[1].links"
              :key="linkIndex"
            >
              <BaseLink class="mt-3" :link="link">
                <span class="link-hover-effect">{{ link.linkText }}</span>
              </BaseLink>
            </template>
          </nav>
        </ClientOnly>

        <!-- Right Column - Logo (Desktop) -->
        <div class="justify-end hidden md:flex">
          <img
            loading="lazy"
            src="/img/optus-logo.png"
            alt="OnePass Mobile logo"
            class="shrink-0 mt-2.5 max-w-full aspect-[1.89] w-[222px]"
          />
        </div>

        <!-- About Section with Logo (Mobile) -->
        <!-- Mobile footer content is never shown - this is redundant but kept for safety -->
        <div v-if="false" class="w-full md:hidden">
          <div
            class="flex items-center justify-between border-b border-gray-600 py-3 cursor-pointer"
            @click="toggleAbout"
          >
            <h3 class="font-medium text-base uppercase">ABOUT</h3>
            <span
              class="transform transition-transform"
              :class="{ 'rotate-180': isAboutOpen }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>

          <div v-show="isAboutOpen" class="py-4 transition-all">
            <img
              loading="lazy"
              src="/img/optus-logo.png"
              alt="Coles Mobile logo"
              class="max-w-full aspect-[1.89] w-[150px]"
            />
          </div>
        </div>
      </div>
    </footer>
  </ClientOnly>
</template>

<style scoped lang="scss">
.link-hover-effect {
  position: relative;
  display: inline-block;
  color: inherit;
  text-decoration: none;
}

.link-hover-effect::after {
  content: "";
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 0;
  left: 0;
  background-color: rgb(var(--primary));
  transition: width 0.3s ease-in-out;
}

.link-hover-effect:hover::after {
  width: 100%;
}
</style>
